"use client";

import { createContext } from "react";
import type { AddCartItemInput, Cart } from "@/types/cart";
import type { CheckoutCustomerPayload } from "@/types/checkout";

// Contrato único do carrinho da vitrine. Implementado por dois adaptadores:
// o legado (WooCommerce, components/Cart/CartProvider.tsx) e o nativo
// (components/Cart/NativeCartProvider.tsx). Quem consome via useCart() não
// sabe qual está ativo, exceto pelas flags de capacidade abaixo.
export type CartBackend = "legacy" | "native";

export interface CartMutationResult {
  success: boolean;
  message: string;
  cart?: Cart;
  aborted?: boolean;
}

export interface CartContextValue {
  backend: CartBackend;
  // Capacidades: o carrinho nativo só tem cupons com NATIVE_COUPONS=on; o frete no
  // carrinho é uma estimativa e só existe com o frete do checkout nativo
  // ligado (senão o frete é calculado no checkout).
  supportsCoupons: boolean;
  supportsCartShipping: boolean;
  // Chaves (item.key) de itens que não podem seguir para o checkout agora
  // (sem preço, sem estoque, despublicado). Sempre vazio no legado.
  unavailableItemKeys: string[];
  cart: Cart | null;
  isHydrated: boolean;
  isLoading: boolean;
  isOpen: boolean;
  error: string;
  pendingItemKey: string | null;
  isCheckoutUpdating: boolean;
  addItem: (
    input: number | AddCartItemInput,
    quantity?: number,
  ) => Promise<{ success: boolean; message: string }>;
  removeItem: (
    key: string,
  ) => Promise<{ success: boolean; message: string }>;
  updateItem: (
    key: string,
    quantity: number,
  ) => Promise<CartMutationResult>;
  updateCustomerAddress: (
    input: CheckoutCustomerPayload,
    signal?: AbortSignal,
  ) => Promise<CartMutationResult>;
  selectShippingRate: (
    packageId: number | string,
    rateId: string,
  ) => Promise<CartMutationResult>;
  calculateShippingPostcode: (
    postcode: string,
    signal?: AbortSignal,
  ) => Promise<CartMutationResult>;
  refreshCart: () => Promise<void>;
  applyCoupon: (code: string) => Promise<CartMutationResult>;
  removeCoupon: (code: string) => Promise<CartMutationResult>;
  // Só no carrinho nativo com cupons ligados: relê o cupom recalculado
  // (inclusive com o carrinho travado pelo checkout).
  refreshCoupon?: () => Promise<void>;
  openCart: () => void;
  closeCart: () => void;
}

export const CartContext = createContext<CartContextValue | null>(null);
